import { Text } from "@softmax/design-system";
import { colors, fonts } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import type { PolicyVersionProgressCheck } from "./policy-progress-records";

export function PolicyProgressResults({
  check,
}: {
  readonly check: Pick<
    PolicyVersionProgressCheck,
    "net" | "interval" | "wins" | "losses" | "neutrals" | "paired"
  >;
}) {
  return check.net === null ? (
    <Text size="small" tone="muted">
      Awaiting paired results
    </Text>
  ) : (
    <>
      <NetComparison net={check.net} interval={check.interval} />
      <Text size="small" tone="muted">
        {check.wins} wins · {check.losses} losses · {check.neutrals} neutral
        over {check.paired} pairs
      </Text>
      <Text size="small" tone="muted">
        {check.interval
          ? `95% net interval: ${check.interval.lo.toFixed(2)} to ${check.interval.hi.toFixed(2)}${check.interval.crossesZero ? " · Includes zero" : ""}`
          : "95% net interval unavailable"}
      </Text>
    </>
  );
}

const netPosition = (value: number) => 8 + (value + 1) * 96;

function NetComparison({
  net,
  interval,
}: {
  readonly net: number;
  readonly interval: PolicyVersionProgressCheck["interval"];
}) {
  return (
    <svg viewBox="0 0 208 40" aria-hidden="true" {...stylex.props(styles.plot)}>
      <g stroke="currentColor" {...stylex.props(styles.axis)}>
        <line x1="8" x2="200" y1="12" y2="12" />
        <line x1="104" x2="104" y1="5" y2="19" />
      </g>
      {interval && (
        <line
          x1={netPosition(interval.lo)}
          x2={netPosition(interval.hi)}
          y1="12"
          y2="12"
          stroke="currentColor"
          strokeWidth="3"
        />
      )}
      <circle cx={netPosition(net)} cy="12" r="4" fill="currentColor" />
      <g {...stylex.props(styles.tick)}>
        <text x="8" y="34">
          −1
        </text>
        <text x="104" y="34" textAnchor="middle">
          0
        </text>
        <text x="200" y="34" textAnchor="end">
          +1
        </text>
      </g>
    </svg>
  );
}

const styles = stylex.create({
  plot: { width: "100%", maxWidth: 360, height: 40, color: colors.chartBlue },
  axis: { color: colors.hairline },
  tick: { fill: colors.subtle, fontFamily: fonts.sans, fontSize: 10 },
});
